
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import dayjs from "dayjs";
import DateTimeRangePicker from "@/views/shouyin/statement/reconciliationDetails/components/SearchPopup.vue";
import {
  JielongSettingForm,
  JieLongStatus,
  JIELONG_STATUS,
  getJielongExtendTimeError,
} from "@/api/jielong";

interface DeliveryOption {
  value: string;
  label: string;
}

@Component({
  components: { DateTimeRangePicker },
})
export default class JielongSettingCard extends Vue {
  @Prop({
    type: Object,
    default: () => ({
      isStorePickup: 1,
      isHomeDelivery: 0,
      startTime: "",
      endTime: "",
      extendTime: "",
      arrivalTime: "",
      minOrderAmount: 0,
      minOrderQuantity: 0,
    }),
  })
  value!: JielongSettingForm;
  @Prop({ type: Boolean, default: false }) readonly!: boolean;
  @Prop({ type: Number, default: JIELONG_STATUS.NOT_STARTED }) status!: JieLongStatus;

  calendarImgSrc = require("@/assets/images/marketActivity/calendar.png");

  deliveryOptions: DeliveryOption[] = [
    { value: "pickup", label: "门店自提" },
    { value: "delivery", label: "送货上门" },
  ];
  deliveryMethods: string[] = ["pickup"];

  startTime: Date | null = null;
  endTime: Date | null = null;
  extendTime: Date | null = null;

  // 接龙开始/结束时间选择器的回显数据
  dateTimeRangeDefaultValue: any = null;

  minDate = new Date(dayjs().subtract(10, 'year').format('YYYY-MM-DD'))
  maxDate = new Date(dayjs().add(10, 'year').format('YYYY-MM-DD'))

  // 抽奖时间选择器限制
  startMinDate = this.minDate
  startMaxDate = this.maxDate
  startMinHour = 1
  startMaxHour = 23
  endMinDate = this.minDate
  endMaxDate = this.maxDate
  endMinHour = 1
  endMaxHour = 23

  get displayStartTime (): string {
    return this.startTime ? this.formatDateTime(this.startTime) : "请选择开始时间";
  }

  get displayEndTime (): string {
    return this.endTime ? this.formatDateTime(this.endTime) : "请选择结束时间";
  }

  get displayExtendTime (): string {
    return this.extendTime ? this.formatDateTime(this.extendTime) : "请选择延长时间";
  }

  get showExtendTime (): boolean {
    return this.status === JIELONG_STATUS.ONGOING;
  }

  purchaseLimitType: "amount" | "quantity" | "" = "";
  purchaseLimitValue = "";
  purchaseLimitError = "";
  private purchaseLimitTouched = false;
  private syncingFromProp = false;

  arrivalTimeValue = "";
  showArrivalPopup = false;
  arrivalCurDate = new Date();

  showExtendPopup = false;
  extendCurDate = new Date();

  fieldErrors: { delivery?: string; time?: string; arrival?: string; extendTime?: string } = {};
  isShake = false;

  @Watch("purchaseLimitValue")
  onPurchaseLimitValueChange (val: string): void {
    let next = val;
    if (this.purchaseLimitType === "quantity") {
      // 起购数量：仅整数，最多两位（2-99）
      next = val.replace(/\D/g, "").slice(0, 2);
    } else {
      // 起购金额：放开 2-99，允许数字和最多两位小数
      next = val.replace(/[^\d.]/g, "");
      const parts = next.split(".");
      if (parts.length > 2) {
        next = `${parts[0]}.${parts.slice(1).join("")}`;
      }
      const [intPart = "", decPart] = next.split(".");
      next = intPart.slice(0, 8) + (decPart != null ? `.${decPart.slice(0, 2)}` : "");
    }
    if (next !== val) {
      this.purchaseLimitValue = next;
      return;
    }
    if (this.purchaseLimitTouched) {
      this.validatePurchaseLimit();
      this.emitFormChange();
    }
  }

  onPurchaseLimitBlur (): void {
    this.purchaseLimitTouched = true;
    this.validatePurchaseLimit();
    this.emitFormChange();
  }

  validatePurchaseLimit (): boolean {
    const value = this.purchaseLimitValue;
    if (!value) {
      this.purchaseLimitError = "";
      return true;
    }
    const num = Number(value);
    if (this.purchaseLimitType === "quantity") {
      if (!Number.isInteger(num) || num < 2 || num > 99) {
        this.purchaseLimitError = "请输入2-99之间的整数";
        return false;
      }
    } else if (Number.isNaN(num) || num <= 0) {
      this.purchaseLimitError = "请输入有效的起购金额";
      return false;
    }
    this.purchaseLimitError = "";
    return true;
  }

  validate (): boolean {
    const errors: { delivery?: string; time?: string; arrival?: string; extendTime?: string } = {};
    if (!this.readonly) {
      if (!this.deliveryMethods.length) {
        errors.delivery = "请选择配送方式";
      }
      if (!this.startTime || !this.endTime) {
        errors.time = "请选择接龙开始和结束时间";
      }
      if (!this.arrivalTimeValue) {
        errors.arrival = "请选择到货时间";
      }
    }
    if (this.showExtendTime) {
      const extendError = getJielongExtendTimeError(this.extendTime, this.endTime);
      if (extendError) errors.extendTime = extendError;
    }
    this.fieldErrors = errors;
    const ok =
      !errors.delivery &&
      !errors.time &&
      !errors.arrival &&
      !errors.extendTime &&
      (this.readonly || this.validatePurchaseLimit());
    if (!ok) this.shake();
    return ok;
  }

  shake (): void {
    this.isShake = true;
    window.setTimeout(() => {
      this.isShake = false;
    }, 1000);
  }

  // 打开接龙开始/结束时间选择弹窗
  openDateTimeRangePicker (field: "start" | "end"): void {
    if (this.readonly) return;
    const isStart = field === "start";
    this.dateTimeRangeDefaultValue = {
      ...this.dateTimeRangeDefaultValue,
      value: isStart ? 1 : 2,
      startDatetimeValueType: isStart ? 1 : 0,
      showStartDatetimePicker: isStart,
      endDatetimeValueType: isStart ? 0 : 1,
      showEndDatetimePicker: !isStart,
    };
    // 等待 defaultValue 更新传递给子组件后再打开弹窗，避免回显数据不同步
    setTimeout(() => {
      (this.$refs.dateTimeRangePickerRef as any).showPopup();
    });
  }

  onDateTimeRangePickerConfirm (res: any): void {
    this.dateTimeRangeDefaultValue = res;
    this.startTime = dayjs(res.starttime).toDate();
    this.endTime = dayjs(res.endtime).toDate();
    this.emitFormChange();
  }

  onDateTimeRangePickerSelectTypeChange (_value: number): void {
    // 当前无需额外处理，预留扩展
  }

  formatDateTime (date: Date): string {
    return dayjs(date).format("YYYY/MM/DD HH:mm");
  }

  /** 开始时间默认：当前时间 */
  private getDefaultStartTime (): Date {
    return dayjs().toDate();
  }

  /** 结束时间默认：次日 23:59 */
  private getDefaultEndTime (): Date {
    return dayjs().add(1, "day").hour(23).minute(59).second(0).millisecond(0).toDate();
  }

  /** 解析时间字符串为选择器回显用的 [dateArray, timeArray] */
  private parseTime (timeStr: string): [string[], string[]] | null {
    const match = timeStr?.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (!match) return null;
    return [
      [match[1], match[2], match[3]],
      [match[4], match[5]],
    ];
  }

  /** 根据当前 startTime/endTime 同步时间选择器回显数据 */
  private syncDateTimeRangeDefaultValue (): void {
    if (!this.startTime || !this.endTime) return;
    const startStr = dayjs(this.startTime).format("YYYY-MM-DD HH:mm:ss");
    const endStr = dayjs(this.endTime).format("YYYY-MM-DD HH:mm:ss");
    const startParsed = this.parseTime(startStr);
    const endParsed = this.parseTime(endStr);
    if (!startParsed || !endParsed) return;
    const [startVal1, startVal2] = startParsed;
    const [endVal1, endVal2] = endParsed;
    this.dateTimeRangeDefaultValue = {
      ...(this.dateTimeRangeDefaultValue || {}),
      startVal1,
      startVal2,
      endVal1,
      endVal2,
      starttime: startStr,
      endtime: endStr,
    };
  }

  openArrivalPopup (): void {
    if (this.readonly) return;
    if (this.arrivalTimeValue) {
      const parsed = dayjs(this.arrivalTimeValue.replace(/\//g, "-"));
      if (parsed.isValid()) {
        this.arrivalCurDate = parsed.toDate();
      }
    } else {
      this.arrivalCurDate = new Date();
    }
    this.showArrivalPopup = true;
  }

  onArrivalCancel (): void {
    this.showArrivalPopup = false;
  }

  onArrivalConfirm (): void {
    this.arrivalTimeValue = dayjs(this.arrivalCurDate).format("YYYY-MM-DD HH:mm");
    this.showArrivalPopup = false;
    this.emitFormChange();
  }

  openExtendPopup (): void {
    if (!this.showExtendTime) return;
    if (this.extendTime) {
      this.extendCurDate = new Date(this.extendTime.getTime());
    } else {
      const now = new Date();
      const end = this.endTime || now;
      const baseMs = Math.max(end.getTime(), now.getTime()) + 60 * 1000;
      this.extendCurDate = new Date(baseMs);
    }
    this.showExtendPopup = true;
  }

  onExtendCancel (): void {
    this.showExtendPopup = false;
  }

  onExtendConfirm (): void {
    this.extendTime = new Date(this.extendCurDate.getTime());
    this.showExtendPopup = false;
    const extendError = getJielongExtendTimeError(this.extendTime, this.endTime);
    if (extendError) {
      this.$set(this.fieldErrors, "extendTime", extendError);
      this.shake();
    } else {
      this.$delete(this.fieldErrors, "extendTime");
    }
    this.emitFormChange();
  }

  mounted (): void {
    this.syncFromValue(this.value);
  }

  @Watch("value", { deep: true })
  onValuePropChange (val: JielongSettingForm): void {
    this.syncFromValue(val);
  }

  @Watch("deliveryMethods")
  onDeliveryMethodsChange (): void {
    this.emitFormChange();
  }

  @Watch("purchaseLimitType")
  onPurchaseLimitTypeChange (): void {
    this.purchaseLimitError = "";
    this.emitFormChange();
  }

  private syncFromValue (val: JielongSettingForm): void {
    if (!val) return;
    this.syncingFromProp = true;
    const methods: string[] = [];
    if (val.isStorePickup === 1) methods.push("pickup");
    if (val.isHomeDelivery === 1) methods.push("delivery");
    this.deliveryMethods = methods.length ? methods : ["pickup"];
    const hasStart = !!val.startTime;
    const hasEnd = !!val.endTime;
    this.startTime = hasStart ? dayjs(val.startTime).toDate() : this.getDefaultStartTime();
    this.endTime = hasEnd ? dayjs(val.endTime).toDate() : this.getDefaultEndTime();
    this.extendTime = val.extendTime ? dayjs(val.extendTime).toDate() : null;
    this.syncDateTimeRangeDefaultValue();
    this.arrivalTimeValue = val.arrivalTime
      ? dayjs(val.arrivalTime).format("YYYY-MM-DD HH:mm")
      : "";
    if ((val.minOrderAmount ?? 0) > 0) {
      this.purchaseLimitType = "amount";
      this.purchaseLimitValue = String(val.minOrderAmount);
    } else if ((val.minOrderQuantity ?? 0) > 1) {
      this.purchaseLimitType = "quantity";
      this.purchaseLimitValue = String(val.minOrderQuantity);
    } else {
      // 金额/数量皆为空时保留当前类型，避免点选「起购金额」后被回写成数量
      this.purchaseLimitValue = "";
    }
    this.$nextTick(() => {
      this.syncingFromProp = false;
      // 新建时无时间则回写默认值到父组件
      if (!hasStart || !hasEnd) {
        this.emitFormChange();
      }
    });
  }

  private emitFormChange (): void {
    if (this.syncingFromProp) return;
    if (this.fieldErrors.delivery && this.deliveryMethods.length) {
      this.$delete(this.fieldErrors, "delivery");
    }
    if (this.fieldErrors.time && this.startTime && this.endTime) {
      this.$delete(this.fieldErrors, "time");
    }
    if (this.fieldErrors.extendTime && !getJielongExtendTimeError(this.extendTime, this.endTime)) {
      this.$delete(this.fieldErrors, "extendTime");
    }
    if (this.fieldErrors.arrival && this.arrivalTimeValue) {
      this.$delete(this.fieldErrors, "arrival");
    }
    const payload: JielongSettingForm = {
      isStorePickup: this.deliveryMethods.includes("pickup") ? 1 : 0,
      isHomeDelivery: this.deliveryMethods.includes("delivery") ? 1 : 0,
      startTime: this.startTime ? dayjs(this.startTime).format("YYYY-MM-DD HH:mm:ss") : "",
      endTime: this.endTime ? dayjs(this.endTime).format("YYYY-MM-DD HH:mm:ss") : "",
      extendTime: this.extendTime ? dayjs(this.extendTime).format("YYYY-MM-DD HH:mm:ss") : "",
      arrivalTime: this.arrivalTimeValue
        ? dayjs(this.arrivalTimeValue.replace(/\//g, "-")).format("YYYY-MM-DD HH:mm:ss")
        : "",
      minOrderAmount:
        this.purchaseLimitType === "amount" ? Number(this.purchaseLimitValue) || 0 : 0,
      minOrderQuantity:
        this.purchaseLimitType === "quantity"
          ? Number(this.purchaseLimitValue) || 0
          : 0,
    };
    this.$emit("input", payload);
  }
}
